/**
 * Where to send someone once they have signed in.
 *
 * Pages hand the return address to /signin in three shapes, and the sign-in page
 * used to read only one of them (`state.from.pathname`), so most people landed
 * on the homepage instead of where they started:
 *
 *   1. `state.from` as a router Location (ProtectedRoute, SignInPrompt, the gates)
 *   2. `state.from` as a plain path string (the old AuthModal, /consultation)
 *   3. `?redirect=` or `?returnUrl=` in the query (listings, membership, complaints,
 *      property create/edit, the invitation page)
 *
 * The query form is attacker-controlled (anyone can send a link to
 * `/signin?redirect=…`), so every candidate goes through `toSafePath`, which only
 * lets through a path on this site. Anything else falls back to the homepage.
 */

export const DEFAULT_REDIRECT = '/';

/** The query parameters older pages use, in the order they are tried. */
const REDIRECT_PARAMS = ['redirect', 'returnUrl'] as const;

/**
 * Pages it makes no sense to return to after signing in. `/signin` would loop;
 * the other two are the sign-up and password-reset entry points.
 */
const AUTH_ENTRY_PATHS = ['/signin', '/register', '/reset-password'];

const MAX_LENGTH = 2048;

/** Any origin works here: it only gives the URL parser something to resolve against. */
const PARSE_BASE = 'https://safaqat24.invalid';
const PARSE_ORIGIN = new URL(PARSE_BASE).origin;

/**
 * Browsers read `\` as `/` and drop tabs and newlines inside URLs, so `/\evil.com`
 * and `/<tab>/evil.com` both become `//evil.com`, another site. Neither a backslash
 * nor any control character belongs in an in-app path.
 */
function hasUnsafeCharacter(path: string): boolean {
  for (let i = 0; i < path.length; i += 1) {
    const code = path.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f || code === 0x5c) return true;
  }
  return false;
}

type LocationLike = { pathname?: unknown; search?: unknown; hash?: unknown };

function withPrefix(part: unknown, prefix: '?' | '#'): string {
  if (typeof part !== 'string' || part === '' || part === prefix) return '';
  return part.startsWith(prefix) ? part : `${prefix}${part}`;
}

function toPathString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const { pathname, search, hash } = value as LocationLike;
    if (typeof pathname !== 'string') return null;
    return `${pathname}${withPrefix(search, '?')}${withPrefix(hash, '#')}`;
  }
  return null;
}

function isAuthEntry(pathname: string): boolean {
  // Routes match case-insensitively and ignore a trailing slash, so compare the same way.
  const normalized = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return AUTH_ENTRY_PATHS.some((entry) => normalized === entry || normalized.startsWith(`${entry}/`));
}

/**
 * Returns `value` as a path on this site (pathname plus any query and hash), or
 * null when it is missing, malformed, points at another origin, or is one of the
 * auth pages. Accepts a path string or a router Location.
 */
export function toSafePath(value: unknown): string | null {
  const path = toPathString(value);
  if (!path || path.length > MAX_LENGTH) return null;
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  if (hasUnsafeCharacter(path)) return null;

  let url: URL;
  try {
    url = new URL(path, PARSE_BASE);
  } catch {
    return null;
  }
  // Belt and braces: whatever the checks above missed, the parser has the final say.
  if (url.origin !== PARSE_ORIGIN) return null;
  // The parsed pathname has dot segments (`/x/../signin`, `%2e%2e`) already resolved.
  if (isAuthEntry(url.pathname)) return null;

  return path;
}

/**
 * The page to open after a successful sign-in.
 *
 * @param state  the router `location.state` that /signin received; its `from` may
 *               be a path string or a Location
 * @param search the `location.search` of /signin, for `?redirect=` / `?returnUrl=`
 * @param fallback where to go when nothing usable was passed
 */
export function resolveFrom(state: unknown, search = '', fallback = DEFAULT_REDIRECT): string {
  if (state && typeof state === 'object' && 'from' in state) {
    const fromState = toSafePath((state as { from?: unknown }).from);
    if (fromState) return fromState;
  }

  if (search) {
    const params = new URLSearchParams(search);
    for (const key of REDIRECT_PARAMS) {
      const fromQuery = toSafePath(params.get(key));
      if (fromQuery) return fromQuery;
    }
  }

  return fallback;
}
